import React, { useState, useEffect } from 'react';
import { updateLeadStatus, updateWpLeadStatus, getPvLeadProperties, getWpLeadProperties, updateLeadProperties, addBoComment } from '../../api';
import {
  getPvConsumptionDisplayFields,
  isYearlyConsumptionOver2700Valid
} from '../../utils/pvConsumptionQualification';

function UpdateStatusDialog({ customerId, userId, currentStatus, onSave, onCancel, productType, leadData }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [leadDataLoading, setLeadDataLoading] = useState(true);
  
  // Status-Daten
  const [formData, setFormData] = useState({
    status: currentStatus || 'Offen'
  });
  
  // Backoffice-Kommentar
  const [boComment, setBoComment] = useState(leadData?.bo_kommentar || '');
  const [boCommentInfo, setBoCommentInfo] = useState(
    leadData?.bo_kommentar_erstellt && leadData?.bo_kommentar_ersteller_name ? {
      ersteller: leadData.bo_kommentar_ersteller_name,
      datum: leadData.bo_kommentar_erstellt
    } : null
  );

  // Produkt-spezifische Daten
  const [productData, setProductData] = useState({});

  // Lead-Daten laden
  useEffect(() => {
    const loadLeadData = async () => {
      setLeadDataLoading(true);
      setError('');

      try {
        let response;
        if (productType === 'PV') {
          response = await getPvLeadProperties(userId, customerId);
        } else if (productType === 'WP') {
          response = await getWpLeadProperties(userId, customerId);
        }

        if (response && response.success && response.properties) {
          // Standardwerte für leere Felder setzen
          let propertiesWithDefaults;
          
          if (productType === 'PV') {
            const consumptionDisplay = getPvConsumptionDisplayFields(response.properties);
            propertiesWithDefaults = {
              ...response.properties,
              eigentumer: response.properties.eigentumer || 'Nein',
              gebaudeart: response.properties.gebaudeart || 'Einfamilienhaus',
              dachausrichtung: response.properties.dachausrichtung || 'Süd',
              dachflache_20_qm: response.properties.dachflache_20_qm || 'Nein',
              dachflache: response.properties.dachflache || '',
              denkmalschutz: response.properties.denkmalschutz || 'Nein',
              verbrauch_ueber_2700: consumptionDisplay.verbrauchUeber2700 || 'Nein',
              verbrauch_2300_2700_3_monate: consumptionDisplay.verbrauch2300_2700_3Monate || 'Nein',
              jahresverbrauch: response.properties.jahresverbrauch || '',
              zukuenftige_energie_abnehmer: response.properties.zukuenftige_energie_abnehmer || 'Nein',
              asbest: response.properties.asbest || 'Nein',
              status_opt_in: response.properties.status_opt_in || 'Offen',
              alternative_montage: response.properties.alternative_montage || 'Nein',
              alternative_montage_flaeche: response.properties.alternative_montage_flaeche || ''
            };
          } else if (productType === 'WP') {
            propertiesWithDefaults = {
              ...response.properties,
              eigentumer: response.properties.eigentumer || 'Nein',
              gebaudeart: response.properties.gebaudeart || 'Einfamilienhaus',
              wohnflache: response.properties.wohnflache || '',
              personen: response.properties.personen || '',
              baujahr: response.properties.baujahr || '',
              gas_oder_oel: response.properties.gas_oder_oel || 'Nein',
              heizung_alter_2: response.properties.heizung_alter_2 || 'Nein',
              waermeabgabesystem: response.properties.waermeabgabesystem || '',
              solarthermie: response.properties.solarthermie || 'Nein',
              solarthermie_abbau: response.properties.solarthermie_abbau || 'Nein',
              denkmalschutz: response.properties.denkmalschutz || 'Nein'
            };
          }
          
          setProductData(propertiesWithDefaults);
          
          // Backoffice-Kommentar aus den Lead-Daten setzen
          if (response.lead) {
            setBoComment(response.lead.bo_kommentar || '');
            if (response.lead.bo_kommentar_erstellt && response.lead.bo_kommentar_ersteller_name) {
              setBoCommentInfo({
                ersteller: response.lead.bo_kommentar_ersteller_name,
                datum: response.lead.bo_kommentar_erstellt
              });
            }
          }
          
          setFormData(prev => ({
            ...prev,
            status: response.properties.status || currentStatus || 'Offen'
          }));
        }
      } catch (err) {
        setError('Fehler beim Laden der Lead-Daten: ' + err.message);
      } finally {
        setLeadDataLoading(false);
      }
    };

    loadLeadData();
  }, [customerId, userId, productType, currentStatus, leadData]);

  const handleStatusChange = (e) => {
    setFormData(prev => ({
      ...prev,
      status: e.target.value
    }));
  };

  const handleProductDataChange = (field, value) => {
    setProductData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');

    try {
      // Status aktualisieren
      let statusResponse;
      if (productType === 'PV') {
        statusResponse = await updateLeadStatus(userId, customerId, formData.status);
      } else if (productType === 'WP') {
        statusResponse = await updateWpLeadStatus(userId, customerId, formData.status);
      } else {
        console.error('Unbekannter productType:', productType);
        setError('Unbekannter Produkttyp');
        return;
      }

      console.log('Status Response:', statusResponse);
      console.log('ProductType:', productType);

      if (!statusResponse || !statusResponse.success) {
        setError(statusResponse?.error || 'Fehler beim Aktualisieren des Status');
        return;
      }

      // Webhook wird bereits automatisch von updateLeadStatus/updateWpLeadStatus im Backend getriggert
      // Kein zusätzlicher Webhook-Aufruf nötig, um doppelte Aufrufe zu vermeiden

      // Backoffice-Kommentar aktualisieren
      if (boComment) {
        const boCommentResponse = await addBoComment(userId, customerId, boComment);
        if (!boCommentResponse.success) {
          setError(boCommentResponse.error || 'Fehler beim Aktualisieren des Backoffice-Kommentars');
          return;
        }
      }

      // Lead-Eigenschaften aktualisieren
      let propertiesData;
      if (productType === 'PV') {
        propertiesData = {
          is_owner: productData.eigentumer === 'Ja' ? 1 : 0,
          building_type: productData.gebaudeart || '',
          roof_orientation: productData.dachausrichtung || '',
          roof_area_over_20: productData.dachflache_20_qm === 'Ja' ? 1 : 0,
          roof_area_size: productData.dachflache || '',
          has_monument_protection: productData.denkmalschutz === 'Ja' ? 1 : 0,
          yearly_consumption_over_2200:
            (productData.verbrauch_ueber_2700 === 'Ja' &&
              isYearlyConsumptionOver2700Valid(productData.jahresverbrauch)) ||
            productData.verbrauch_2300_2700_3_monate === 'Ja'
              ? 1
              : 0,
          yearly_consumption: productData.jahresverbrauch || '',
          has_future_energy_consumers: productData.zukuenftige_energie_abnehmer === 'Ja' ? 1 : 0,
          has_asbestos: productData.asbest === 'Ja' ? 1 : 0,
          is_qualified: productData.status_opt_in === 'Bestätigt' ? 1 : 0,
          has_alternative_mounting: productData.alternative_montage === 'Ja' ? 1 : 0,
          alternative_mounting_area: productData.alternative_montage_flaeche || ''
        };
      } else if (productType === 'WP') {
        propertiesData = {
          is_owner: productData.eigentumer === 'Ja' ? 1 : 0,
          building_type: productData.gebaudeart || '',
          living_area_size: productData.wohnflache || '',
          people_in_house: productData.personen || '',
          build_year: productData.baujahr || '',
          has_gas_or_oil: productData.gas_oder_oel === 'Ja' ? 1 : 0,
          heating_older_than_2_years: productData.heizung_alter_2 === 'Ja' ? 1 : 0,
          current_heating_system: productData.waermeabgabesystem || '',
          has_solar_thermal: productData.solarthermie === 'Ja' ? 1 : 0,
          solar_thermal_removed: productData.solarthermie_abbau === 'Ja' ? 1 : 0,
          has_monument_protection: productData.denkmalschutz === 'Ja' ? 1 : 0,
          is_qualified: 1
        };
      }

      const propertiesResponse = await updateLeadProperties(userId, customerId, productType, propertiesData);

      if (!propertiesResponse.success) {
        setError(propertiesResponse.error || 'Fehler beim Aktualisieren der Lead-Eigenschaften');
        return;
      }

      // Erfolgreich gespeichert
      if (onSave) {
        onSave();
      }
    } catch (err) {
      setError('Fehler beim Speichern: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await handleSave();
  };

  const statusOptions = [
    'Offen',
    'Qualifiziert',
    'Final nicht erreicht',
    'Kein Interesse mehr',
    'Will nicht innerhalb 1 Jahres Umsätzen',
    'Widerruf',
    'Telefonnummer ungültig',
    'Test Lead',
    'Kwh zu niedrig',
    'Denkmalschutz',
    'Dach muss saniert werden',
    'Technisch nicht möglich',
    'Dublette'
  ];

  const renderBooleanSelect = (value, onChange, label) => (
    <div className="form-field">
      <label>{label}</label>
      <select
        value={value || 'Nein'}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="Nein">Nein</option>
        <option value="Ja">Ja</option>
      </select>
    </div>
  );

  const renderTextInput = (value, onChange, label, placeholder = '') => (
    <div className="form-field">
      <label>{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );

  const renderSelectInput = (value, onChange, label, options) => (
    <div className="form-field">
      <label>{label}</label>
      <select
        value={value || options[0]}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map(option => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
    </div>
  );

  const renderProductForm = () => {
    if (productType === 'PV') {
      return (
        <div className="product-form-section">
          <h3>Status & Kommentare</h3>
          
          <div className="form-field">
            <label>Status</label>
            <select
              value={formData.status}
              onChange={handleStatusChange}
            >
              {statusOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div className="form-field bo-comment-field">
            <label>Backoffice-Kommentar</label>
            {boCommentInfo && (
              <div className="bo-comment-info">
                <small>
                  Letzter Kommentar von {boCommentInfo.ersteller} <br />
                   am {new Date(boCommentInfo.datum).toLocaleDateString('de-DE', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </small>
              </div>
            )}
            <textarea
              value={boComment}
              onChange={(e) => setBoComment(e.target.value)}
              placeholder="Interner Kommentar für das Backoffice..."
              rows="5"
              className={boComment ? 'has-content' : ''}
            />
          </div>

          <h3>Immobilien-Details</h3>
          <div className="form-grid">
            {renderBooleanSelect(
              productData.eigentumer,
              (value) => handleProductDataChange('eigentumer', value),
              'Eigentümer'
            )}
            
            {renderSelectInput(
              productData.gebaudeart,
              (value) => handleProductDataChange('gebaudeart', value),
              'Gebäudeart',
              ['Einfamilienhaus', 'Mehrfamilienhaus', 'Reihenhaus', 'Doppelhaushälfte', 'Sonstiges']
            )}
            
            {renderBooleanSelect(
              productData.dachflache_20_qm,
              (value) => handleProductDataChange('dachflache_20_qm', value),
              'Dachfläche über 20 qm'
            )}
            
            {renderTextInput(
              productData.dachflache,
              (value) => handleProductDataChange('dachflache', value),
              'Dachfläche (qm)',
              'z.B. 50'
            )}
            
            {renderSelectInput(
              productData.dachausrichtung,
              (value) => handleProductDataChange('dachausrichtung', value),
              'Dachausrichtung',
              ['Süd', 'Süd-West', 'Süd-Ost', 'West', 'Ost', 'Nord']
            )}
            
            {renderBooleanSelect(
              productData.alternative_montage,
              (value) => handleProductDataChange('alternative_montage', value),
              'Alternative Montage möglich'
            )}
            
            {productData.alternative_montage === 'Ja' && renderTextInput(
              productData.alternative_montage_flaeche,
              (value) => handleProductDataChange('alternative_montage_flaeche', value),
              'Alternative Montagefläche',
              'z.B. Garage, Carport'
            )}
            
            {renderBooleanSelect(
              productData.denkmalschutz,
              (value) => handleProductDataChange('denkmalschutz', value),
              'Denkmalschutz'
            )}
            
            {renderSelectInput(
              productData.verbrauch_ueber_2700,
              (value) => handleProductDataChange('verbrauch_ueber_2700', value),
              'kWh-Verbrauch > 2700?',
              ['Ja', 'Nein']
            )}

            {renderSelectInput(
              productData.verbrauch_2300_2700_3_monate,
              (value) => handleProductDataChange('verbrauch_2300_2700_3_monate', value),
              'kWh 2300–2700 – Realisierung in 3 Monaten?',
              ['Ja', 'Nein']
            )}
            
            {renderTextInput(
              productData.jahresverbrauch,
              (value) => handleProductDataChange('jahresverbrauch', value),
              'Jahresverbrauch (kWh)',
              'z.B. 3000'
            )}
            
            {renderBooleanSelect(
              productData.zukuenftige_energie_abnehmer,
              (value) => handleProductDataChange('zukuenftige_energie_abnehmer', value),
              'Zukünftige Energie-Abnehmer'
            )}
            
            {renderBooleanSelect(
              productData.asbest,
              (value) => handleProductDataChange('asbest', value),
              'Asbest vorhanden'
            )}
            
            {renderSelectInput(
              productData.status_opt_in,
              (value) => handleProductDataChange('status_opt_in', value),
              'Opt-In Status',
              ['Bestätigt', 'Ausstehend', 'Abgelehnt']
            )}
          </div>
        </div>
      );
    } else if (productType === 'WP') {
      return (
        <div className="product-form-section">
          <h3>Status & Kommentare</h3>
          
          <div className="form-field">
            <label>Status</label>
            <select
              value={formData.status}
              onChange={handleStatusChange}
            >
              {statusOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div className="form-field bo-comment-field">
            <label>Backoffice-Kommentar</label>
            {boCommentInfo && (
              <div className="bo-comment-info">
                <small>
                  Letzter Kommentar von {boCommentInfo.ersteller} 
                  am {new Date(boCommentInfo.datum).toLocaleDateString('de-DE', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </small>
              </div>
            )}
            <textarea
              value={boComment}
              onChange={(e) => setBoComment(e.target.value)}
              placeholder="Interner Kommentar für das Backoffice..."
              rows="5"
              className={boComment ? 'has-content' : ''}
            />
          </div>

          <h3>Wärmepumpen-Eigenschaften</h3>
          <div className="form-grid">
            {renderBooleanSelect(
              productData.eigentumer,
              (value) => handleProductDataChange('eigentumer', value),
              'Eigentümer'
            )}
            
            {renderSelectInput(
              productData.gebaudeart,
              (value) => handleProductDataChange('gebaudeart', value),
              'Gebäudeart',
              ['Einfamilienhaus', 'Mehrfamilienhaus', 'Reihenhaus', 'Doppelhaushälfte', 'Sonstiges']
            )}
            
            {renderTextInput(
              productData.wohnflache,
              (value) => handleProductDataChange('wohnflache', value),
              'Wohnfläche (qm)',
              'z.B. 120'
            )}
            
            {renderTextInput(
              productData.personen,
              (value) => handleProductDataChange('personen', value),
              'Personen im Haushalt',
              'z.B. 4'
            )}
            
            {renderTextInput(
              productData.baujahr,
              (value) => handleProductDataChange('baujahr', value),
              'Baujahr',
              'z.B. 1995'
            )}
            
            {renderBooleanSelect(
              productData.gas_oder_oel,
              (value) => handleProductDataChange('gas_oder_oel', value),
              'Gas oder Öl Heizung vorhanden'
            )}
            
            {renderBooleanSelect(
              productData.heizung_alter_2,
              (value) => handleProductDataChange('heizung_alter_2', value),
              'Heizung älter als 2 Jahre'
            )}
            
            {renderSelectInput(
              productData.waermeabgabesystem,
              (value) => handleProductDataChange('waermeabgabesystem', value),
              'Wärmeabgabesystem',
              ['Fußbodenheizung', 'Heizkörper', 'Beides', 'Sonstiges']
            )}
            
            {renderBooleanSelect(
              productData.solarthermie,
              (value) => handleProductDataChange('solarthermie', value),
              'Solarthermie vorhanden'
            )}
            
            {productData.solarthermie === 'Ja' && renderBooleanSelect(
              productData.solarthermie_abbau,
              (value) => handleProductDataChange('solarthermie_abbau', value),
              'Solarthermie-Abbau möglich'
            )}
            
            {renderBooleanSelect(
              productData.denkmalschutz,
              (value) => handleProductDataChange('denkmalschutz', value),
              'Denkmalschutz'
            )}
          </div>
        </div>
      );
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-dialog extended">
        <div className="modal-header">
          <h2>{productType}-Status und Eigenschaften bearbeiten</h2>
          <button className="close-button" onClick={onCancel}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="modal-content">
          {leadDataLoading ? (
            <div className="loading-center">
              <div className="loading-spinner"></div>
              <span>Lade {productType}-Daten...</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {error && <div className="error-message">{error}</div>}
              
              {renderProductForm()}

              <div className="modal-actions">
                <button type="button" onClick={onCancel} className="cancel-button">
                  Abbrechen
                </button>
                <button type="submit" disabled={loading} className="save-button">
                  {loading ? 'Wird gespeichert...' : 'Speichern'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          padding: 20px;
        }
        
        .modal-dialog.extended {
          background: white;
          border-radius: 12px;
          max-width: 900px;
          width: 100%;
          max-height: 90vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
        }
        
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #e2e8f0;
          background: #f8fafc;
          flex-shrink: 0;
        }
        
        .modal-header h2 {
          margin: 0;
          color: #2d3748;
          font-size: 1.5rem;
        }
        
        .close-button {
          background: none;
          border: none;
          cursor: pointer;
          color: #718096;
          padding: 4px;
          border-radius: 4px;
          transition: all 0.2s;
        }
        
        .close-button:hover {
          background: #e2e8f0;
          color: #2d3748;
        }
        
        .modal-content {
          flex: 1;
          overflow-y: auto;
          padding: 24px;
        }
        
        .loading-center {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px;
          gap: 12px;
          color: #718096;
        }
        
        .loading-spinner {
          width: 32px;
          height: 32px;
          border: 3px solid #e2e8f0;
          border-top: 3px solid #4a6de5;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          100% { transform: rotate(360deg); }
        }
        
        .product-form-section {
          background: #f8fafc;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 24px;
          border: 1px solid #e2e8f0;
        }
        
        .product-form-section h3 {
          margin: 0 0 20px 0;
          color: #2d3748;
          font-size: 1.1rem;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 16px;
        }
        
        .form-field {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        
        .form-field.bo-comment-field {
          margin-bottom: 20px;
        }
        
        .form-field.bo-comment-field textarea {
          height: 120px;
        }
        
        .form-field label {
          font-weight: 600;
          color: #4a5568;
          font-size: 0.9rem;
        }
        
        .form-field input,
        .form-field select,
        .form-field textarea {
          padding: 10px 12px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          font-size: 0.9rem;
          transition: border-color 0.2s;
          background: white;
        }
        
        .form-field input:focus,
        .form-field select:focus,
        .form-field textarea:focus {
          outline: none;
          border-color: #4a6de5;
          box-shadow: 0 0 0 3px rgba(74, 109, 229, 0.1);
        }
        
        .modal-actions {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
          margin-top: 24px;
          padding-top: 20px;
          border-top: 1px solid #e2e8f0;
        }
        
        .cancel-button,
        .save-button {
          padding: 12px 24px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s;
          font-size: 0.9rem;
        }
        
        .cancel-button {
          background: #f3f4f6;
          color: #4b5563;
        }
        
        .cancel-button:hover {
          background: #e5e7eb;
        }
        
        .save-button {
          background: #4a6de5;
          color: white;
        }
        
        .save-button:hover:not(:disabled) {
          background: #3c5ce0;
        }
        
        .save-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .error-message {
          color: #e53e3e;
          background: #fed7d7;
          padding: 12px;
          border-radius: 6px;
          margin-bottom: 20px;
          font-size: 0.9rem;
          border-left: 4px solid #e53e3e;
        }
        
        .bo-comment-info {
          margin-bottom: 8px;
          color: #666;
          font-style: italic;
          background-color: #f8f9fa;
          padding: 8px;
          border-radius: 4px;
          border-left: 3px solid #007bff;
        }
        
        .bo-comment-field textarea.has-content {
          border-color: #28a745;
        }
        
        @media (max-width: 768px) {
          .modal-dialog.extended {
            max-width: 95%;
            margin: 10px;
            max-height: 95vh;
          }
          
          .modal-header {
            padding: 16px 20px;
          }
          
          .modal-header h2 {
            font-size: 1.25rem;
          }
          
          .modal-content {
            padding: 20px;
          }
          
          .form-grid {
            grid-template-columns: 1fr;
            gap: 12px;
          }
          
          .product-form-section {
            padding: 16px;
            margin-bottom: 20px;
          }
          
          .modal-actions {
            flex-direction: column;
            gap: 8px;
          }
          
          .cancel-button,
          .save-button {
            width: 100%;
          }
        }
        
        @media (max-width: 480px) {
          .modal-overlay {
            padding: 10px;
          }
          
          .modal-dialog.extended {
            max-width: 100%;
            max-height: 98vh;
          }
          
          .modal-header {
            padding: 12px 16px;
          }
          
          .modal-content {
            padding: 16px;
          }
          
          .product-form-section {
            padding: 12px;
          }
          
          .form-field input,
          .form-field select {
            padding: 8px 10px;
          }
        }
      `}</style>
    </div>
  );
}

export default UpdateStatusDialog;